import { useState, useCallback } from "react";
import {
    Title,
    Paper,
    Textarea,
    NumberInput,
    Button,
    Group,
    Stack,
    Alert,
    Accordion,
    Text,
    Badge,
    Grid,
    Divider,
    JsonInput,
    ScrollArea,
    Select,
    Checkbox,
    Tabs,
    Drawer,
    TextInput,
    MultiSelect
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { MdSearch, MdInfo, MdWarning, MdClear } from "react-icons/md";
import { useMsal } from "@azure/msal-react";
import { getToken, useLogin } from "../../../authConfig";
import { searchAdminApi, getCitationFilePath } from "../../../api";
import { SearchAdminRequest, SearchAdminResponse, RetrievalMode, Domain } from "../../../api/models";
import { useTranslation } from "react-i18next";
import { CitationCard } from "../../../components/citation-list/CitationCard";
import { AssetViewer } from "../../../components/assetviewer";
import { Citation } from "../../../components/types";
import { countries } from "../../../components/country-selector/countries";

const AzureSearchPage = () => {
    const { t } = useTranslation();
    const [queryText, setQueryText] = useState("");
    const [referenceNumber, setReferenceNumber] = useState("");
    const [sourceFile, setSourceFile] = useState("");
    const [cloudinaryID, setCloudinaryID] = useState("");
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [retrieveCount, setRetrieveCount] = useState(6);
    const [useSemanticRanker, setUseSemanticRanker] = useState(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState(false);
    const [useQueryRewriting, setUseQueryRewriting] = useState(false);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(0);
    const [assetTypes, setAssetTypes] = useState<string[]>(["algolia", "intellum", "orbit", "playbook", "pm360"]);
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null); // null = no country filter (default)
    const [selectedDomain, setSelectedDomain] = useState<Domain>("human"); // the index is always scoped to one domain

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<SearchAdminResponse | null>(null);
    const [searchDuration, setSearchDuration] = useState<number | null>(null);
    const [selectedCitation, setSelectedCitation] = useState<Citation | undefined>(undefined);
    const [opened, { toggle, close }] = useDisclosure(false);

    const numOfSearchCriteris = [queryText, referenceNumber, sourceFile, cloudinaryID].filter(value => value.trim()).length;

    const client = useLogin ? useMsal().instance : undefined;

    const onCitationClick = useCallback(
        (citation: Citation) => {
            toggle();
            setSelectedCitation(citation);
        },
        [toggle]
    );

    const handleSearch = async () => {
        setLoading(true);
        setError(null);
        setResults(null);
        setSearchDuration(null);

        const startTime = performance.now();

        try {
            const idToken = client ? await getToken(client) : undefined;

            // Map retrieval mode to search flags
            const useTextSearch = retrievalMode === RetrievalMode.Text || retrievalMode === RetrievalMode.Hybrid;
            const useVectorSearch = retrievalMode === RetrievalMode.Vectors || retrievalMode === RetrievalMode.Hybrid;

            const request: SearchAdminRequest = {
                query_text: queryText,
                reference_number: referenceNumber,
                top: retrieveCount,
                merge_chunks: false,
                use_text_search: useTextSearch,
                use_vector_search: useVectorSearch,
                use_semantic_ranker: useSemanticRanker,
                use_semantic_captions: useSemanticCaptions,
                use_query_rewriting: useQueryRewriting,
                minimum_search_score: minimumSearchScore,
                minimum_reranker_score: minimumRerankerScore,
                source_file: sourceFile,
                cloudinary_id: cloudinaryID,
                sources: assetTypes,
                selected_countries: selectedCountry ? [selectedCountry] : [],
                domain: selectedDomain
            };

            const response = await searchAdminApi(request, idToken);
            const endTime = performance.now();
            setSearchDuration(endTime - startTime);
            setResults(response);
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred while searching");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack gap="lg" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Paper withBorder radius="lg" shadow="sm" p="lg" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Stack gap="lg" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <Stack gap={8}>
                        <Title order={3}>Azure AI Search</Title>
                        <Text size="sm" c="dimmed">
                            Test Azure AI Search queries directly and see raw results
                        </Text>
                    </Stack>

                    <Stack gap="md">
                        <Textarea
                            label="Search Query"
                            placeholder="Enter your search query..."
                            value={queryText}
                            onChange={e => setQueryText(e.currentTarget.value)}
                            minRows={3}
                            autosize
                            maxRows={8}
                        />

                        <TextInput
                            label="Reference number"
                            placeholder="Search by reference number (exact match)"
                            value={referenceNumber}
                            onChange={e => setReferenceNumber(e.currentTarget.value)}
                        />

                        <TextInput
                            label="Source file"
                            placeholder="Search by source file"
                            value={sourceFile}
                            onChange={e => setSourceFile(e.currentTarget.value)}
                        />

                        <TextInput
                            label="Cloudinary ID"
                            placeholder="Search by Cloudinary ID"
                            value={cloudinaryID}
                            onChange={e => setCloudinaryID(e.currentTarget.value)}
                        />

                        <Grid>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Select
                                    label={t("labels.retrievalMode.label")}
                                    description={t("helpTexts.retrievalMode")}
                                    value={retrievalMode}
                                    data={[
                                        {
                                            label: t("labels.retrievalMode.options.hybrid"),
                                            value: RetrievalMode.Hybrid
                                        },
                                        { label: t("labels.retrievalMode.options.vectors"), value: RetrievalMode.Vectors },
                                        { label: t("labels.retrievalMode.options.texts"), value: RetrievalMode.Text }
                                    ]}
                                    onChange={val => setRetrievalMode(val as RetrievalMode)}
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <NumberInput
                                    label={t("labels.retrieveCount")}
                                    description={t("helpTexts.retrieveNumber")}
                                    value={retrieveCount}
                                    onChange={val => setRetrieveCount(Number(val) || 6)}
                                    min={1}
                                    max={100}
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <NumberInput
                                    label={t("labels.minimumSearchScore")}
                                    description={t("helpTexts.searchScore")}
                                    value={minimumSearchScore}
                                    onChange={val => setMinimumSearchScore(Number(val) || 0)}
                                    min={0}
                                    max={1}
                                    step={0.01}
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <NumberInput
                                    label={t("labels.minimumRerankerScore")}
                                    description={t("helpTexts.rerankerScore")}
                                    value={minimumRerankerScore}
                                    onChange={val => setMinimumRerankerScore(Number(val) || 0)}
                                    min={1}
                                    max={4}
                                    step={0.1}
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <MultiSelect
                                    label={t("labels.assetTypes")}
                                    description={t("helpTexts.assetTypes")}
                                    data={["algolia", "intellum", "orbit", "playbook", "pm360"]}
                                    value={assetTypes}
                                    onChange={ev => setAssetTypes(ev)}
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Select
                                    label="Country"
                                    description="Filter results by country (no filter when empty)"
                                    placeholder="All countries"
                                    clearable
                                    searchable
                                    value={selectedCountry}
                                    data={countries
                                        .toSorted((a, b) => a.name.localeCompare(b.name))
                                        .map(c => ({ value: c.isoCode, label: `${c.name} (${c.isoCode})` }))}
                                    onChange={setSelectedCountry}
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                                <Select
                                    label="Domain"
                                    description="Results are always scoped to one domain"
                                    allowDeselect={false}
                                    value={selectedDomain}
                                    data={[
                                        { value: "human", label: t("domain.human") },
                                        { value: "vet", label: t("domain.vet") }
                                    ]}
                                    onChange={val => setSelectedDomain((val as Domain) ?? "human")}
                                />
                            </Grid.Col>
                        </Grid>

                        <Divider label="Search Options" labelPosition="center" />

                        <Grid>
                            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                                <Checkbox
                                    label={t("labels.useSemanticRanker")}
                                    description={t("helpTexts.useSemanticReranker")}
                                    checked={useSemanticRanker}
                                    onChange={e => setUseSemanticRanker(e.currentTarget.checked)}
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                                <Checkbox
                                    label={t("labels.useSemanticCaptions")}
                                    description={t("helpTexts.useSemanticCaptions")}
                                    checked={useSemanticCaptions}
                                    onChange={e => setUseSemanticCaptions(e.currentTarget.checked)}
                                />
                            </Grid.Col>
                        </Grid>

                        <Group gap="xs">
                            <Button
                                onClick={handleSearch}
                                loading={loading}
                                disabled={numOfSearchCriteris == 0 || numOfSearchCriteris > 1 || loading}
                                leftSection={<MdSearch size={16} />}
                            >
                                Search
                            </Button>
                            <Button
                                onClick={() => {
                                    setQueryText("");
                                    setReferenceNumber("");
                                    setSourceFile("");
                                    setCloudinaryID("");
                                    setSelectedCountry(null);
                                    setSelectedDomain("human");
                                    setResults(null);
                                    setError(null);
                                    setSearchDuration(null);
                                }}
                                disabled={numOfSearchCriteris === 0 || loading}
                                variant="light"
                                color="gray"
                                leftSection={<MdClear size={16} />}
                            >
                                Clear
                            </Button>
                        </Group>
                    </Stack>

                    {(error || results) && <Divider />}

                    {error && (
                        <Alert icon={<MdWarning />} title="Error" color="red" variant="light">
                            {error}
                        </Alert>
                    )}

                    {results && (
                        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
                            <Group justify="space-between">
                                <Title order={4}>Search Results</Title>
                                <Group gap="xs">
                                    {searchDuration !== null && (
                                        <Badge size="lg" variant="light" color="gray">
                                            {(searchDuration / 1000).toFixed(2)}s
                                        </Badge>
                                    )}
                                    <Badge size="lg" variant="filled">
                                        {results.total_results} results
                                    </Badge>
                                </Group>
                            </Group>

                            {results.results.length === 0 ? (
                                <Alert icon={<MdInfo />} title="No Results" color="blue" variant="light">
                                    No documents were found matching your search criteria.
                                </Alert>
                            ) : (
                                <ScrollArea style={{ flex: 1 }}>
                                    <Accordion variant="separated">
                                        {results.results.map((result, index) => {
                                            const lastModifiedTimestamp = result.lastModifiedTimestamp
                                                ? new Date(Date.parse(result.lastModifiedTimestamp))
                                                : null;
                                            const path = getCitationFilePath(result.sourcefile);
                                            const citation: Citation = {
                                                footnoteNumber: index + 1,
                                                title: result.title,
                                                path: path,
                                                sourceFile: result.sourcefile,
                                                score: result.score,
                                                rerankerScore: result.reranker_score,
                                                thumbnailUrl: result.thumbnailUrl,
                                                lastModifiedTimestamp: result.lastModifiedTimestamp,
                                                source: result,
                                                tooltip: {
                                                    title: result.title || "-",
                                                    cloudinaryID: result.cloudinaryID || "-",
                                                    publicationId: result.publicationId,
                                                    mediaType: result.mediaType || "-",
                                                    interestAreas: result.interestAreas?.join(", ") || "-",
                                                    category: result.resourceTypes?.join(", ") || "-",
                                                    score: result.score ?? 0,
                                                    rerankerScore: result.reranker_score ?? 0,
                                                    lastModifiedTimestamp: lastModifiedTimestamp,
                                                    thumbnailUrl: result.thumbnailUrl,
                                                    referenceNumber: result.referenceNumber,
                                                    publicationUrl: result.publicationUrl
                                                },
                                                mergedChunks: false
                                            };

                                            return (
                                                <Accordion.Item key={result.id || index} value={`result-${index}`}>
                                                    <Accordion.Control>
                                                        <Group justify="space-between" wrap="nowrap">
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <Text fw={500} truncate>
                                                                    {result.title || result.sourcefile || `Result ${index + 1}`}
                                                                </Text>
                                                                <Group gap="xs" mt={4}>
                                                                    <Badge size="sm" variant="light">
                                                                        {result.source}
                                                                    </Badge>

                                                                    {result.referenceNumber && (
                                                                        <Badge size="sm" variant="light" color="gray">
                                                                            {result.referenceNumber}
                                                                        </Badge>
                                                                    )}

                                                                    {result.mediaType && (
                                                                        <Badge size="sm" variant="light" color="gray">
                                                                            {result.mediaType}
                                                                        </Badge>
                                                                    )}
                                                                    {result.score && (
                                                                        <Badge size="sm" color="blue">
                                                                            Score: {result.score.toFixed(3)}
                                                                        </Badge>
                                                                    )}
                                                                    {result.reranker_score && (
                                                                        <Badge size="sm" color="green">
                                                                            Reranker: {result.reranker_score.toFixed(3)}
                                                                        </Badge>
                                                                    )}
                                                                </Group>
                                                            </div>
                                                        </Group>
                                                    </Accordion.Control>
                                                    <Accordion.Panel>
                                                        <Grid gap="md">
                                                            <Grid.Col span={{ base: 12, md: 4 }}>
                                                                <CitationCard citation={citation} onClick={onCitationClick} />
                                                            </Grid.Col>
                                                            <Grid.Col span={{ base: 12, md: 8 }}>
                                                                <Tabs defaultValue="overview">
                                                                    <Tabs.List>
                                                                        <Tabs.Tab value="overview">Overview</Tabs.Tab>
                                                                        <Tabs.Tab value="content">Content</Tabs.Tab>
                                                                        <Tabs.Tab value="metadata">JSON</Tabs.Tab>
                                                                    </Tabs.List>

                                                                    <Tabs.Panel value="overview" pt="md">
                                                                        <ScrollArea h={400}>
                                                                            <Stack gap="md">
                                                                                <div>
                                                                                    <Text fw={500} size="sm" mb={4}>
                                                                                        Title
                                                                                    </Text>
                                                                                    <Text size="sm">{result.title || "-"}</Text>
                                                                                </div>

                                                                                <div>
                                                                                    <Text fw={500} size="sm" mb={4}>
                                                                                        Source
                                                                                    </Text>
                                                                                    <Text size="sm">{result.source || "-"}</Text>
                                                                                </div>

                                                                                <div>
                                                                                    <Text fw={500} size="sm" mb={4}>
                                                                                        Media Type
                                                                                    </Text>
                                                                                    <Text size="sm">{result.mediaType || "-"}</Text>
                                                                                </div>

                                                                                <div>
                                                                                    <Text fw={500} size="sm" mb={4}>
                                                                                        Reference Number
                                                                                    </Text>
                                                                                    <Text size="sm">{result.referenceNumber || "-"}</Text>
                                                                                </div>

                                                                                <div>
                                                                                    <Text fw={500} size="sm" mb={4}>
                                                                                        Category
                                                                                    </Text>
                                                                                    <Text size="sm">{result.category || "-"}</Text>
                                                                                </div>

                                                                                {result.resourceTypes && result.resourceTypes.length > 0 && (
                                                                                    <div>
                                                                                        <Text fw={500} size="sm" mb={4}>
                                                                                            Resource Types
                                                                                        </Text>
                                                                                        <Group gap="xs">
                                                                                            {result.resourceTypes.map((type, idx) => (
                                                                                                <Badge key={idx} variant="light">
                                                                                                    {type}
                                                                                                </Badge>
                                                                                            ))}
                                                                                        </Group>
                                                                                    </div>
                                                                                )}

                                                                                {result.interestAreas && result.interestAreas.length > 0 && (
                                                                                    <div>
                                                                                        <Text fw={500} size="sm" mb={4}>
                                                                                            Interest Areas
                                                                                        </Text>
                                                                                        <Group gap="xs">
                                                                                            {result.interestAreas.map((area, idx) => (
                                                                                                <Badge key={idx} variant="light" color="blue">
                                                                                                    {area}
                                                                                                </Badge>
                                                                                            ))}
                                                                                        </Group>
                                                                                    </div>
                                                                                )}

                                                                                <div>
                                                                                    <Text fw={500} size="sm" mb={4}>
                                                                                        Language
                                                                                    </Text>
                                                                                    <Text size="sm">{result.language || "-"}</Text>
                                                                                </div>

                                                                                <div>
                                                                                    <Text fw={500} size="sm" mb={4}>
                                                                                        Last Modified
                                                                                    </Text>
                                                                                    <Text size="sm">
                                                                                        {lastModifiedTimestamp
                                                                                            ? lastModifiedTimestamp.toLocaleDateString()
                                                                                            : "-"}
                                                                                    </Text>
                                                                                </div>

                                                                                <Divider />

                                                                                <div>
                                                                                    <Text fw={500} size="sm" mb={4}>
                                                                                        Scores
                                                                                    </Text>
                                                                                    <Group gap="md">
                                                                                        {result.score && (
                                                                                            <div>
                                                                                                <Text size="xs" c="dimmed">
                                                                                                    Search Score
                                                                                                </Text>
                                                                                                <Text size="sm">{result.score.toFixed(4)}</Text>
                                                                                            </div>
                                                                                        )}
                                                                                        {result.reranker_score && (
                                                                                            <div>
                                                                                                <Text size="xs" c="dimmed">
                                                                                                    Reranker Score
                                                                                                </Text>
                                                                                                <Text size="sm">{result.reranker_score.toFixed(4)}</Text>
                                                                                            </div>
                                                                                        )}
                                                                                    </Group>
                                                                                </div>

                                                                                {result.publicationId && (
                                                                                    <>
                                                                                        <Divider label="Publication Details" />
                                                                                        <div>
                                                                                            <Text fw={500} size="sm" mb={4}>
                                                                                                Publication ID
                                                                                            </Text>
                                                                                            <Text size="sm">{result.publicationId}</Text>
                                                                                        </div>
                                                                                        {result.publicationPubMedId && (
                                                                                            <div>
                                                                                                <Text fw={500} size="sm" mb={4}>
                                                                                                    PubMed ID
                                                                                                </Text>
                                                                                                <Text size="sm">{result.publicationPubMedId}</Text>
                                                                                            </div>
                                                                                        )}
                                                                                        {result.publicationJournal && (
                                                                                            <div>
                                                                                                <Text fw={500} size="sm" mb={4}>
                                                                                                    Journal
                                                                                                </Text>
                                                                                                <Text size="sm">{result.publicationJournal}</Text>
                                                                                            </div>
                                                                                        )}
                                                                                        {result.publicationUrl && (
                                                                                            <div>
                                                                                                <Text fw={500} size="sm" mb={4}>
                                                                                                    Publication URL
                                                                                                </Text>
                                                                                                <Text
                                                                                                    size="sm"
                                                                                                    component="a"
                                                                                                    href={result.publicationUrl}
                                                                                                    target="_blank"
                                                                                                    style={{ color: "var(--mantine-color-blue-6)" }}
                                                                                                >
                                                                                                    {result.publicationUrl}
                                                                                                </Text>
                                                                                            </div>
                                                                                        )}
                                                                                    </>
                                                                                )}

                                                                                {result.intellumUrl && (
                                                                                    <>
                                                                                        <Divider label="Intellum Details" />
                                                                                        <div>
                                                                                            <Text fw={500} size="sm" mb={4}>
                                                                                                Organization
                                                                                            </Text>
                                                                                            <Text size="sm">{result.intellumOrganization || "-"}</Text>
                                                                                        </div>
                                                                                        {result.intellumPresenters && (
                                                                                            <div>
                                                                                                <Text fw={500} size="sm" mb={4}>
                                                                                                    Presenters
                                                                                                </Text>
                                                                                                <Text size="sm">{result.intellumPresenters}</Text>
                                                                                            </div>
                                                                                        )}
                                                                                        <div>
                                                                                            <Text fw={500} size="sm" mb={4}>
                                                                                                Intellum URL
                                                                                            </Text>
                                                                                            <Text
                                                                                                size="sm"
                                                                                                component="a"
                                                                                                href={result.intellumUrl}
                                                                                                target="_blank"
                                                                                                style={{ color: "var(--mantine-color-blue-6)" }}
                                                                                            >
                                                                                                {result.intellumUrl}
                                                                                            </Text>
                                                                                        </div>
                                                                                    </>
                                                                                )}

                                                                                <div>
                                                                                    <Text fw={500} size="sm" mb={4}>
                                                                                        File Information
                                                                                    </Text>
                                                                                    <Stack gap="xs">
                                                                                        <Text size="sm">
                                                                                            <Text span fw={500}>
                                                                                                Source File:
                                                                                            </Text>{" "}
                                                                                            {result.sourcefile}
                                                                                        </Text>
                                                                                        <Text size="sm">
                                                                                            <Text span fw={500}>
                                                                                                Cloudinary ID:
                                                                                            </Text>{" "}
                                                                                            {result.cloudinaryID || "-"}
                                                                                        </Text>
                                                                                    </Stack>
                                                                                </div>
                                                                            </Stack>
                                                                        </ScrollArea>
                                                                    </Tabs.Panel>

                                                                    <Tabs.Panel value="content" pt="md">
                                                                        <ScrollArea h={400}>
                                                                            {result.content ? (
                                                                                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                                                                                    {result.content}
                                                                                </Text>
                                                                            ) : (
                                                                                <Text size="sm" c="dimmed">
                                                                                    No content available
                                                                                </Text>
                                                                            )}
                                                                        </ScrollArea>
                                                                    </Tabs.Panel>

                                                                    <Tabs.Panel value="metadata" pt="md">
                                                                        <JsonInput
                                                                            value={JSON.stringify(result, null, 2)}
                                                                            readOnly
                                                                            minRows={20}
                                                                            styles={{
                                                                                input: {
                                                                                    fontFamily: "monospace",
                                                                                    fontSize: "12px",
                                                                                    height: "400px",
                                                                                    overflow: "auto"
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Tabs.Panel>
                                                                </Tabs>
                                                            </Grid.Col>
                                                        </Grid>
                                                    </Accordion.Panel>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </ScrollArea>
                            )}
                        </Stack>
                    )}
                </Stack>
            </Paper>
            <Drawer
                opened={opened}
                onClose={close}
                position="right"
                size="80%"
                title={selectedCitation?.title}
                styles={{
                    body: {
                        height: "calc(100vh - 7rem)"
                    }
                }}
            >
                <AssetViewer citation={selectedCitation} />
            </Drawer>
        </Stack>
    );
};

export default AzureSearchPage;
